import React, { useState, useEffect, useRef, useMemo } from "react";
import { AiOutlineLoading3Quarters, AiOutlineClose } from "react-icons/ai";
import { ToastContainer, toast } from "react-toastify";
import { BiPhone } from "react-icons/bi";
import { motion, AnimatePresence } from "framer-motion";
import { FaSearch, FaFileExcel } from "react-icons/fa";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import ExcelHandler from "./ExcelHandler"; // Assuming same ExcelHandler as CRM
import { FIRST_MESSAGE, CALL_PROMPT } from "../../utils/callConfig";

const WaitingList = ({ user }) => {
  const navigate = useNavigate();
  const [waitingList, setWaitingList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [loadingState, setLoadingState] = useState({});
  const [showCallModal, setShowCallModal] = useState(false);
  const [callStatus, setCallStatus] = useState("idle");
  const [currentContact, setCurrentContact] = useState(null);
  const [callTime, setCallTime] = useState(0);
  const [isAutoCalling, setIsAutoCalling] = useState(false);
  const isAutoCallingRef = useRef(false);
  const importFileInputRef = useRef(null);

  const [callSettings, setCallSettings] = useState({
      startHour: 8,
      startMinute: 45,
      endHour: 16,
      endMinute: 45
    });
  // Sync auto-calling ref
  useEffect(() => {
    isAutoCallingRef.current = isAutoCalling;
  }, [isAutoCalling]);



 useEffect(() => {
    axios.get(`${process.env.REACT_APP_SERVER_API}/api/settings/calling-hours?userId=${user.id}`)
      .then(response => {
        if (response.data.callSettings) {
          setCallSettings(response.data.callSettings);
          console.log("response.data.callSettings", response.data.callSettings);

        }
      })
      .catch(err => console.error('Error fetching calling hours:', err));
  }, [user.id]);


  // Fetch waiting list
  const fetchWaitingList = async () => {
    if (!user?.id || !user?.role) {
      toast.error("User data is missing");
      return;
    }
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_API}/api/waiting-list?userId=${user.id}&role=${user.role}`
      );
    // Deduplicate by toNumber, keeping most recent entry
    // const uniqueEntries = [];
    // const seenNumbers = new Set();
    // const sortedData = response.data.sort(
    //   (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    // );
    // for (const item of sortedData) {
    //   if (!seenNumbers.has(item.toNumber)) {
    //     seenNumbers.add(item.toNumber);
    //     uniqueEntries.push({
    //       srNo: uniqueEntries.length + 1,
    //       ...item,
    //       attemptCount: item.attemptCount || 0,
    //       toNumber: item.toNumber || item.to,
    //     });
    //   }
    // }
    setWaitingList(response.data);
    } catch (error) {
      toast.error(`Failed to fetch waiting list: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Load waiting list on mount
  useEffect(() => {
    fetchWaitingList();
  }, [user]);

  // Excel handling
  const { handleImportExcel, handleExportExcel } = ExcelHandler({
    fetchContacts: fetchWaitingList,
    user,
  });

  // Manual call handler (same as CRM.js)
  const handleCall = async (contact) => {
    if (!contact.toNumber || contact.toNumber === contact.to) {
      toast.error(`Cannot call ${contact.to}: No valid number`);
      return false;
    }

    setCurrentContact(contact);
    setShowCallModal(true);
    setCallStatus("calling");
    setLoadingState((prev) => ({ ...prev, [contact._id]: true }));

    try {
      const checkResponse = await axios.post(
        `${process.env.REACT_APP_SERVER_API}/api/check-calling-hours`,
        {
          phoneNumber: contact.toNumber,
          name: contact.to,
          userId: user.id,
        }
      );

      if (!checkResponse.data.canCall) {
        toast.warn(`Call to ${contact.to} added to waiting list (outside calling hours)`);
        setCallStatus("Pending");
        await axios.put(
          `${process.env.REACT_APP_SERVER_API}/api/waiting-list/${contact._id}`,
          {
            status: "",
            attemptCount: (contact.attemptCount || 0) + 1,
          }
        );
        fetchWaitingList();
        return false;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_API}/api/create-call`,
        {
          toNumber: contact.toNumber,
          name: contact.to,
          first_message: FIRST_MESSAGE,
          prompt: CALL_PROMPT,
          userId: user.id,
        },
        { headers: { "Content-Type": "application/json" } }
      );

      const { callId, callSid } = response.data;
      localStorage.setItem("currentCallSid", callSid);
      localStorage.setItem("currentCallId", callId);
      setCallStatus("connected");
      navigate(
        `/call/${encodeURIComponent(contact.to)}/${encodeURIComponent(
          contact.toNumber
        )}`
      );

      const startTime = Date.now();
      const timer = setInterval(() => {
        setCallTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);

      await axios.put(
        `${process.env.REACT_APP_SERVER_API}/api/waiting-list/${contact._id}`,
        {
          status: "connected",
          attemptCount: (contact.attemptCount || 0) + 1,
        }
      );

      fetchWaitingList();
      return true;
    } catch (error) {
      console.error("Call failed:", error);
      setCallStatus("error");
      toast.error(`Call to ${contact.to} failed: ${error.message}`);
      await axios.put(
        `${process.env.REACT_APP_SERVER_API}/api/waiting-list/${contact._id}`,
        {
          status: "no-answer",
          attemptCount: (contact.attemptCount || 0) + 1,
        }
      );
      fetchWaitingList();
      return false;
    } finally {
      setLoadingState((prev) => ({ ...prev, [contact._id]: false }));
    }
  };

  // Auto-calling logic (same as CRM.js)
  const startAutoCalling = async () => {
    if (isAutoCallingRef.current) {
      toast.warn("Auto-calling is already running");
      return;
    }

    if (!user?.id || !user?.role) {
      toast.error("User data is missing");
      return;
    }

    setIsAutoCalling(true);
    isAutoCallingRef.current = true;
    setShowCallModal(true);
    toast.info("Auto-calling started");

    try {
      while (isAutoCallingRef.current) {
        const response = await axios.post(
          `${process.env.REACT_APP_SERVER_API}/api/start-auto-call-from-waiting-list`,
          { userId: user.id, role: user.role }
        );

        if (response.data.done) {
          toast.warn(response.data.message || "No pending entries");
          break;
        }

        const { contact } = response.data;
        if (!contact?._id || !contact?.toNumber || !contact?.to) {
          toast.error("Invalid contact data, skipping");
          await new Promise((resolve) => setTimeout(resolve, 2000));
          continue;
        }

        setCurrentContact(contact);
        setCallStatus("calling");
        setLoadingState((prev) => ({ ...prev, [contact._id]: true }));

        const callResult = await handleCall(contact);

        if (callResult) {
          toast.success(`Call to ${contact.to} connected`);
          const timer = setInterval(() => {
            if (callStatus === "completed") {
              clearInterval(timer);
            }
          }, 500);
        } else if (callStatus === "waiting") {
          // Already handled in handleCall
        } else {
          toast.warn(`Call to ${contact.to} not answered`);
        }

        setLoadingState((prev) => ({ ...prev, [contact._id]: false }));

        if (isAutoCallingRef.current) {
          toast.info("Preparing next call in 8 seconds...");
          await new Promise((resolve) => setTimeout(resolve, 8000));
        }
      }
    } catch (error) {
      toast.error(`Auto-calling stopped: ${error.message}`);
    } finally {
      setIsAutoCalling(false);
      isAutoCallingRef.current = false;
      setShowCallModal(false);
      setCurrentContact(null);
      setCallStatus("idle");
      localStorage.removeItem("currentCallSid");
      localStorage.removeItem("currentCallId");
      toast.info("Auto-calling completed");
    }
  };

// Format calling hours for display
const formatCallingTime = ({ hour, minute }) => {
  const period = hour >= 12 ? 'PM' : 'AM';
  const formattedHour = hour % 12 || 12;
  return `${formattedHour}:${minute.toString().padStart(2, '0')} ${period}`;
};


  // Stop auto-calling
  const stopAutoCalling = () => {
    // if (window.confirm("Are you sure you want to stop auto-calling?")) {
      setIsAutoCalling(false);
      isAutoCallingRef.current = false;
      setShowCallModal(false);
      toast.info("Auto-calling stopped");
      fetchWaitingList();
    // }
  };

  // Format call time
  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // Filter and paginate data
  const filteredData = useMemo(() => {
    return Array.isArray(waitingList)
      ? waitingList.filter(
          (row) =>
            row.to?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            row.toNumber?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : [];
  }, [waitingList, searchTerm]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const currentData = filteredData.slice(
    indexOfLastItem - itemsPerPage,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      localStorage.removeItem("currentCallSid");
      localStorage.removeItem("currentCallId");
    };
  }, []);

  return (
    <motion.div
      className="w-full h-[calc(100vh-4rem)] sm:h-[calc(100vh-5rem)] overflow-y-auto hide-scrollbar p-4 sm:p-6 lg:p-8 bg-gray-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div className="max-w-9xl">
        {/* Header */}
        <div className="bg-gray-50 p-4 mb-6 rounded-xl flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-800">Redialer List</h1>


          <div className="text-gray-600 text-base">
            <span className="font-bold">Calling Hours:</span>{' '}
            <span className="ml-1">
              {formatCallingTime({ hour: callSettings.startHour, minute: callSettings.startMinute })} –{' '}
              {formatCallingTime({ hour: callSettings.endHour, minute: callSettings.endMinute })}
            </span>
          </div>






        </div>

        {/* Search and Buttons */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            {/* <button
              onClick={() => handleExportExcel(waitingList)}
              className="flex items-center gap-2 text-black px-4 py-2 rounded-sm shadow-md transition"
            >
              <FaFileExcel className="w-5 h-5 text-green-500" /> Export to Excel
            </button>
            */}
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={startAutoCalling}
              disabled={isAutoCalling}
              className={`flex items-center gap-2 px-4 py-2 rounded-sm shadow-md transition ${
                isAutoCalling
                  ? "bg-gray-400"
                  : "bg-green-500 text-white hover:bg-green-600"
              }`}
            >
              <BiPhone className="w-5 h-5" /> Start Calling
            </button>
            <button
              onClick={stopAutoCalling}
              // disabled={!isAutoCalling}
              className={`flex items-center gap-2 px-4 py-2 rounded-sm shadow-md transition ${
                !isAutoCalling
                  ? "bg-gray-400"
                  : "bg-red-500 text-white hover:bg-red-600"
              }`}
            >
              <AiOutlineClose className="w-5 h-5" /> Stop Calling
            </button>
            <div className="relative w-64">
              <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                placeholder="Search by name or number"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-white border border-gray-300 rounded-lg pl-12 pr-8 py-2 shadow-md"
              />
              {searchTerm && (
                <AiOutlineClose
                  onClick={() => setSearchTerm("")}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer"
                />
              )}
            </div>
          </div>
        </div>

        {/* Loading Overlay */}
        {isLoading && (
          <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center z-50 animate-pulse">
            <AiOutlineLoading3Quarters
              className="animate-spin text-white"
              size={48}
            />
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-blue-400 text-white">
              <tr>
                {[
                  "Sr No",
                  "From",
                  "Name",
                  "Phone",
                  "Status",
                  "Attempts",
                  "Timestamp",
                  "Actions",
                ].map((header) => (
                  <th
                    key={header}
                    className="px-4 py-3 text-md font-semibold text-center"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentData.length > 0 ? (
                currentData.map((row ,i) => (
                  <tr
                    key={row._id}
                    className="hover:bg-gray-100 transition-all"
                  >
                    <td className="px-4 py-3 text-md text-center">{i +1}</td>
                    <td className="px-4 py-3 text-md text-center">
                      {row.from || "N/A"}
                    </td>
                    <td className="px-4 py-3 text-md text-center">{row.to}</td>
                    <td className="px-4 py-3 text-md text-center">
                      {row.toNumber }
                    </td>
                    <td className="px-4 py-3 text-md text-center">
                      {row.status}
                    </td>
                    <td className="px-4 py-3 text-md text-center">
                      {row.attemptCount}
                    </td>
                    <td className="px-4 py-3 text-md text-center">
                      {new Date(row.timestamp).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-md text-center">
                      <button
                        onClick={() => handleCall(row)}
                        className="text-blue-600 hover:text-blue-800 disabled:opacity-50"
                        disabled={loadingState[row._id] || !row.toNumber}
                      >
                        {loadingState[row._id] ? (
                          <AiOutlineLoading3Quarters className="animate-spin h-6 w-6" />
                        ) : (
                          <BiPhone className="h-6 w-6" />
                        )}
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="8"
                    className="text-center py-8 text-gray-500"
                  >
                    No waiting list data found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex justify-center mt-8 space-x-2">
          <button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-4 py-2 bg-gray-200 rounded-md disabled:opacity-50 hover:bg-gray-300"
          >
            Previous
          </button>
          {Array.from({ length: totalPages }, (_, idx) => (
            <button
              key={idx}
              onClick={() => paginate(idx + 1)}
              className={`px-4 py-2 rounded-md ${
                currentPage === idx + 1
                  ? "bg-blue-400 text-white"
                  : "bg-gray-200 hover:bg-gray-300"
              }`}
            >
              {idx + 1}
            </button>
          ))}
          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-4 py-2 bg-gray-200 rounded-md disabled:opacity-50 hover:bg-gray-300"
          >
            Next
          </button>
        </div>

        {/* Call Modal */}
        <AnimatePresence>
          {showCallModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                className="bg-white rounded-xl p-8 max-w-md w-full"
              >
                <div className="text-center space-y-4">
                  <h3 className="text-xl font-semibold">
                    {currentContact?.to || "Unknown"}
                  </h3>
                  <p className="text-gray-600">
                    {currentContact?.toNumber || "No Number"}
                  </p>
                  <div className="text-lg font-medium text-gray-700">
                    {callStatus === "calling" && (
                      <div className="flex items-center justify-center space-x-2">
                        <span>Calling...</span>
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" />
                          <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce delay-100" />
                          <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce delay-200" />
                        </div>
                      </div>
                    )}
                    {callStatus === "connected" && formatTime(callTime)}
                    {callStatus === "error" && "Call Failed"}
                    {callStatus === "waiting" && "Waiting (Outside Hours)"}
                  </div>
                  <button
                    onClick={() => {
                      setShowCallModal(false);
                      if (callStatus === "connected") {
                        setCallStatus("completed");
                      }
                    }}
                    className="w-full bg-red-600 text-white py-3 rounded-full hover:bg-red-700 transition-colors"
                  >
                    {callStatus === "error" || callStatus === "waiting"
                      ? "Close"
                      : "End Call"}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <ToastContainer position="top-right" autoClose={3000} />
      </div>
    </motion.div>
  );
};

export default WaitingList;